/**
 * `<page-server-config>` — the server settings editor, routed at
 * `/instances/:id/server-config`.
 *
 * Implements `SC-F612` (the customer-owned half of `server.yaml` as a form),
 * `SC-F613` (the control-plane-owned half, refused rather than ignored, shown
 * read-only *with its reason*) and `SC-F614` (the mirror of Sluice's own
 * `ServerConfig.Validate()`, so a typo is refused at publish instead of at
 * container boot). Stories: `SC-S611`, `SC-S612`, `SC-S613`, and `SC-S301`'s
 * fourth criterion for the derived DuckDB numbers.
 *
 * # Three things this screen must never get wrong
 *
 * **1. A `server.yaml` change is restart-class, in its entirety, permanently.**
 * Nothing in that file hot-reloads — not the limits, not the policy engine, not the
 * log level (`docs/operations/hot-reload.md`). So the page does not decide per field
 * whether a change is expensive; it says once, at the top and again at the save, that
 * publishing any of this replaces the running process. Saving is *not* the restart:
 * saving writes the draft, and the publish that ships the draft is what costs the
 * outage. Conflating the two would either frighten a reader out of an edit or
 * surprise them with an interruption.
 *
 * **2. A guarded key is refused, not dropped, and it is displayed rather than
 * hidden.** `SC-S612`'s second criterion is explicit: a control-plane-owned field is
 * shown read-only *with a short reason*, and a secret shows only that it is set. A
 * field that simply vanished would read as a product that had not thought about it,
 * and the customer would then ask support for the thing they cannot see. The
 * refusal is identical for an owner — a guard a role can talk its way past is not a
 * guard.
 *
 * **3. The two query timeouts are clamped to 8 seconds and raising them is
 * acknowledged, not merely allowed.** Sluice's REST drain is a hard-coded 10 s and
 * its shutdown timeout is not configurable, so a query permitted to run longer than
 * the drain is *dropped* on every restart-class publish — and every publish that
 * touches this file is restart-class. That is the one upstream defect the plan
 * classifies as unmitigated (`SLUICE-U20`), and linter rule L33 is a blocking
 * warning cleared by an acknowledgement rather than a refusal, because a customer
 * who queries slowly and publishes rarely is making a legitimate trade. This form
 * therefore lets the value be raised and makes the trade explicit first.
 *
 * # The `spec` is a `server.yaml`-shaped object, and the field table below is its
 * schema
 *
 * `PUT` takes the customer-owned keys under their own `server.yaml` spelling —
 * `limits.maxQueryTimeout`, `cache.rewrite.ttl` — because that is the spelling the
 * `SC-F613` guard compares dotted paths against and the spelling the rendered file
 * uses, so a reader can hold the form and the YAML view side by side. [FIELDS] is
 * transcribed from `03` §2.5's customer-owned table and its key names are checked
 * against `internal/bundle/serverconfig.go`'s own emitted keys, which is the only
 * place that decides them.
 *
 * A key the server did not send is rendered **empty with its documented default as
 * a placeholder**, and is not written into the request body. `PUT` replaces the
 * customer-owned half, so inventing a value for an absent key would silently
 * rewrite a setting nobody touched; leaving it absent lets the server's own
 * defaulting — which starts from `bundle.DefaultCustomerFields` rather than from a
 * zero value, deliberately — do its job.
 */

import { LitElement, css, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import {
  toast,
  type BnBadgeVariant,
  type BnColumn,
  type BnKvRow,
  type BnRow,
  type BnSelectChangeDetail,
  type BnSelectOption,
} from "../components/index.js";

import { ApiError, errorDetail, parseError, validationFields } from "../api/errors.js";
import {
  DRAIN_CLAMP_SECONDS,
  GUARDED_KEYS,
  getGenerationYaml,
  getServerConfigWithETag,
  getServerConfigYaml,
  guardReason,
  putServerConfig,
  type ServerConfig,
} from "../api/generations.js";
import { getInstance, sizeClass, type Instance } from "../api/instances.js";
import type { PolicyFinding } from "../api/policies.js";
import { hrefFor, navigate } from "../nav.js";

/** The bundle path of the rendered server configuration. It is a separate artifact
 * from the policy directory with its own hash, which is why it has its own view. */
const SERVER_CONFIG_PATH = "server.yaml";

/** How a field is edited. `members` is an ordered multi-select — the composite
 * engine's member list, where order decides which engine's deny wins. */
type FieldKind = "int" | "float" | "duration" | "bool" | "string" | "enum" | "members";

/** One customer-owned key. */
interface Field {
  /** The dotted `server.yaml` path, which is also the request body's shape. */
  path: string;
  label: string;
  kind: FieldKind;
  /** Why this exists and what it costs. Rendered as the field's hint, so the
   * consequence is next to the input rather than in a paragraph above it. */
  hint: string;
  /** The documented default, shown as the input's placeholder when the server sent
   * no value. It is never written into the request body. */
  placeholder?: string;
  /** Inclusive lower bound for a number. */
  min?: number;
  /** Inclusive upper bound for a number. */
  max?: number;
  options?: BnSelectOption[];
  /** Set for a key the pinned build accepts and does not act on. It stays editable
   * and is badged, because the value must survive into the generation that turns
   * the behaviour on — dropping it would silently lose a customer's setting. */
  inert?: string;
}

/** One card of the form. */
interface Group {
  id: string;
  title: string;
  /** The paragraph above the fields: what this block does and what it costs. */
  blurb: TemplateResult;
  fields: Field[];
}

const LOG_LEVELS: BnSelectOption[] = [
  { value: "debug", label: "debug", description: "Every request, with policy decisions." },
  { value: "info", label: "info", description: "The default." },
  { value: "warn", label: "warn" },
  { value: "error", label: "error" },
];

const ENGINES: BnSelectOption[] = [
  {
    value: "yaml",
    label: "yaml",
    description: "The manifest engine. What every policy you author here compiles into.",
  },
  {
    value: "opa",
    label: "opa",
    disabled: true,
    disabledReason:
      "Rego module management is a later release, so an OPA engine would load with no modules at all — which in Sluice means deny everything.",
  },
  {
    value: "composite",
    label: "composite",
    description:
      "Runs several engines in order. With only the yaml member it behaves identically to yaml.",
  },
];

const COMPOSITE_MEMBERS: BnSelectOption[] = [
  { value: "yaml", label: "yaml", description: "The manifest engine." },
  {
    value: "opa",
    label: "opa",
    disabled: true,
    disabledReason: "Rego module management is a later release.",
  },
  {
    value: "rebac",
    label: "rebac",
    disabled: true,
    disabledReason:
      "Relationship policies are a later release. Without this member a RelationshipPolicy loads and does nothing, silently, which is why the member is not offered before the editor is.",
  },
];

const TRACING_PROTOCOLS: BnSelectOption[] = [
  { value: "grpc", label: "grpc", description: "The default." },
  { value: "http", label: "http" },
];

/**
 * FIELDS is `03` §2.5's customer-owned table, grouped the way an operator reads it
 * rather than the way `server.yaml` orders its blocks.
 *
 * Every path here is checked against the keys `internal/bundle/serverconfig.go`
 * actually emits, because that renderer is the only thing that decides them and a
 * path this form spelled differently would be a field that saved into nothing.
 * `logging.format`, `admin.enabled`, `duckdb.tempDir`, `approval.stateDir` and
 * `budget.stateDir` are absent because they are constants in the renderer with no
 * field to hold them — a stronger guarantee than a guard, and the reason they are
 * not in the guarded list either.
 */
const FIELDS: readonly Group[] = [
  {
    id: "limits",
    title: "Query limits",
    blurb: html`<p class="copy">
      The envelope every query runs inside. These are the instance's own ceilings; a
      <code>QueryRewritePolicy</code> can lower them further for a matched subject, and nothing can
      raise them past what is set here.
    </p>`,
    fields: [
      {
        path: "limits.maxRows",
        label: "Default row cap",
        kind: "int",
        min: 1,
        placeholder: "100000",
        hint: "Applied when a client asks for no limit of its own.",
      },
      {
        path: "limits.maxRowsCeiling",
        label: "Row cap ceiling",
        kind: "int",
        min: 1,
        placeholder: "100000",
        hint: "The clamp on a client's own max_rows. Must be at least the default cap, or a client could ask for less than the default and get more.",
      },
      {
        path: "limits.maxSqlBytes",
        label: "Maximum SQL size (bytes)",
        kind: "int",
        min: 1,
        placeholder: "1048576",
        hint: "A statement larger than this is refused before parsing.",
      },
      {
        path: "limits.queryTimeout",
        label: "Query timeout",
        kind: "duration",
        placeholder: "8s",
        hint: "Rendered at 8s rather than Sluice's own 30s. A duration such as 8s, 1500ms or 1m.",
      },
      {
        path: "limits.maxQueryTimeout",
        label: "Maximum enforced query timeout",
        kind: "duration",
        placeholder: "8s",
        hint: "The ceiling a policy's own timeout clamp is measured against. Must be at least the query timeout. Above 8s this is a deliberate trade — see the note below.",
      },
      {
        path: "limits.maxConcurrent",
        label: "Concurrent queries",
        kind: "int",
        min: 1,
        placeholder: "100",
        hint: "Queries in flight at once. Beyond it, requests queue rather than failing.",
      },
      {
        path: "limits.disableCrossCatalog",
        label: "Reject multi-catalog queries",
        kind: "bool",
        hint: "A query touching two catalogs is refused with ACL_REJECTED rather than planned across both.",
      },
    ],
  },
  {
    id: "rate",
    title: "Rate limits",
    blurb: html`<p class="copy">
      Token buckets in front of the query path. Zero is <strong>off</strong>, not unlimited: the
      limiter is only constructed when the rate is positive.
    </p>`,
    fields: [
      {
        path: "limits.globalRps",
        label: "Instance requests per second",
        kind: "float",
        min: 0,
        placeholder: "500",
        hint: "Across every subject. 0 disables the bucket.",
      },
      {
        path: "limits.globalBurst",
        label: "Instance burst",
        kind: "int",
        min: 0,
        placeholder: "1000",
        hint: "How far above the sustained rate a spike may go.",
      },
      {
        path: "limits.defaultSubjectRps",
        label: "Per-subject requests per second",
        kind: "float",
        min: 0,
        placeholder: "0",
        hint: "The fallback for subjects whose binding carries no rateLimit of its own. 0 disables it.",
      },
      {
        path: "limits.defaultSubjectBurst",
        label: "Per-subject burst",
        kind: "int",
        min: 0,
        placeholder: "0",
        hint: "The fallback burst for those same subjects.",
      },
      {
        path: "limits.perIpBurst",
        label: "Per-IP burst",
        kind: "int",
        min: 0,
        placeholder: "0",
        hint: "Kept editable so the value survives into the generation that turns the per-IP bucket on.",
        inert:
          "The per-IP rate is held at 0 and is control-plane-owned: every request reaches the container through the shared edge proxy, so the only address the bucket would ever see is the proxy's — a positive rate would limit every client of the instance as though they were one. This burst is therefore inert today.",
      },
      {
        path: "limits.perIpMaxBuckets",
        label: "Per-IP bucket table size",
        kind: "int",
        min: 0,
        placeholder: "10000",
        hint: "How many distinct addresses the limiter tracks before evicting.",
        inert:
          "Inert for the same reason as the per-IP burst: the limiter is never constructed while the per-IP rate is 0.",
      },
    ],
  },
  {
    id: "rest",
    title: "Request handling",
    blurb: html`<p class="copy">
      The HTTP envelope. <code>Request timeout</code> is load-bearing twice: it participates in the
      drain contract, and it is the ceiling the approval synchronous wait is clamped against.
    </p>`,
    fields: [
      {
        path: "rest.maxBodyBytes",
        label: "Maximum request body (bytes)",
        kind: "int",
        min: 1,
        placeholder: "1048576",
        hint: "A larger body is refused before it is read.",
      },
      {
        path: "rest.requestTimeout",
        label: "Request timeout",
        kind: "duration",
        placeholder: "30s",
        hint: "The whole-request budget. Sluice's drain is a fixed 10s regardless of what is set here, so a request permitted to run past roughly 10s is dropped on every restart-class publish.",
      },
    ],
  },
  {
    id: "pool",
    title: "Connection pool",
    blurb: html`<p class="copy">
      The engine's own connection pool. The memory limit and the thread count are
      <em>not</em> here: they are derived from the instance's size class together with the
      container's limits, and are listed below as control-plane-owned.
    </p>`,
    fields: [
      {
        path: "duckdb.maxOpen",
        label: "Maximum open connections",
        kind: "int",
        min: 1,
        placeholder: "4",
        hint: "Shown read-only elsewhere when derived from the size class; editable here to tune it within that size.",
      },
      {
        path: "duckdb.maxIdle",
        label: "Maximum idle connections",
        kind: "int",
        min: 0,
        placeholder: "2",
        hint: "Idle connections kept for reuse rather than reopened.",
      },
      {
        path: "duckdb.connMaxIdle",
        label: "Idle connection lifetime",
        kind: "duration",
        placeholder: "5m",
        hint: "How long an idle connection is kept before it is closed.",
      },
    ],
  },
  {
    id: "cache",
    title: "Rewrite cache",
    blurb: html`<p class="copy">
      Caches the rewritten form of a query so the policy engine does not re-plan an identical
      request. <strong>Any policy carrying a CEL condition forces full-header cache keying</strong>,
      which is a cost paid on every query the instance serves and not only on the matching ones — so
      a cache and a condition-heavy policy set work against each other.
    </p>`,
    fields: [
      {
        path: "cache.rewrite.enabled",
        label: "Enable the rewrite cache",
        kind: "bool",
        hint: "Off by default.",
      },
      {
        path: "cache.rewrite.size",
        label: "Entries",
        kind: "int",
        min: 1,
        placeholder: "4096",
        hint: "Rewritten statements held at once.",
      },
      {
        path: "cache.rewrite.ttl",
        label: "Entry lifetime",
        kind: "duration",
        placeholder: "60s",
        hint: "How long a rewritten statement stays valid. A published policy change invalidates the cache regardless.",
      },
    ],
  },
  {
    id: "engine",
    title: "Policy engine",
    blurb: html`<p class="copy">
      Which engine decides a query. Changing it changes <strong>evaluation semantics</strong>, not
      just performance, so it is confirmed separately from the rest of the form. Rego modules and
      relationship policies are later releases; their engine members are shown with the reason they
      cannot be selected rather than hidden, because hiding them makes the question unanswerable.
    </p>`,
    fields: [
      {
        path: "policies.engine",
        label: "Engine",
        kind: "enum",
        options: ENGINES,
        placeholder: "yaml",
        hint: "The manifests you author here are the yaml engine's input.",
      },
      {
        path: "policies.composite.members",
        label: "Composite members",
        kind: "members",
        hint: "Order matters: the first non-abstained deny wins, and column masks are first-member-wins. The order is the order you pick them in — to reorder, clear the selection and pick again.",
      },
      {
        path: "policies.rebac.cacheTtl",
        label: "Relationship cache lifetime",
        kind: "duration",
        placeholder: "10s",
        hint: "Only meaningful with the composite rebac member.",
      },
      {
        path: "policies.rebac.cacheSize",
        label: "Relationship cache entries",
        kind: "int",
        min: 0,
        placeholder: "10000",
        hint: "Only meaningful with the composite rebac member.",
      },
    ],
  },
  {
    id: "logging",
    title: "Logging",
    blurb: html`<p class="copy">
      The container's own log level. The <em>format</em> is fixed to JSON and is
      control-plane-owned, because the log pipeline parses it.
    </p>`,
    fields: [
      {
        path: "logging.level",
        label: "Level",
        kind: "enum",
        options: LOG_LEVELS,
        placeholder: "info",
        hint: "debug logs every request with its policy decision, which on a busy instance is a lot of volume.",
      },
    ],
  },
  {
    id: "tracing",
    title: "Tracing",
    blurb: html`<p class="copy">
      OTLP span export to a collector you run. Enabling it <strong>without an endpoint is a boot
      failure</strong> in Sluice, so it is refused here at publish instead — discovering it when the
      container exits is an outage, and a mysterious one.
    </p>`,
    fields: [
      {
        path: "tracing.enabled",
        label: "Export traces",
        kind: "bool",
        hint: "Off by default.",
      },
      {
        path: "tracing.endpoint",
        label: "Collector endpoint",
        kind: "string",
        placeholder: "otel-collector.internal:4317",
        hint: "Required whenever tracing is enabled.",
      },
      {
        path: "tracing.protocol",
        label: "Protocol",
        kind: "enum",
        options: TRACING_PROTOCOLS,
        placeholder: "grpc",
        hint: "grpc or http. Anything else is refused at boot.",
      },
      {
        path: "tracing.insecure",
        label: "Skip TLS verification",
        kind: "bool",
        hint: "For a collector on the same private network with no certificate.",
      },
      {
        path: "tracing.sampleRatio",
        label: "Sample ratio",
        kind: "float",
        min: 0,
        max: 1,
        placeholder: "1",
        hint: "Between 0 and 1 inclusive. Outside that range the configuration is refused.",
      },
    ],
  },
  {
    id: "approval",
    title: "Approvals",
    blurb: html`<p class="copy">
      The timers behind an <code>ApprovalPolicy</code>. The <em>destination</em> of an approval
      notification is deliberately not here — it is a selection over the organization's webhook
      subscriptions, which have an SSRF validator, a signing secret and a delivery log, rather than a
      URL typed into a config file.
    </p>`,
    fields: [
      {
        path: "approval.syncWait",
        label: "Synchronous wait",
        kind: "duration",
        placeholder: "20s",
        hint: "How long a query waits inline for a decision before it becomes asynchronous. Sluice clamps this to the request timeout minus 2s; the effective value is shown beneath.",
      },
      {
        path: "approval.requestTtl",
        label: "Request lifetime",
        kind: "duration",
        placeholder: "15m",
        hint: "How long an unanswered approval request stays pending.",
      },
      {
        path: "approval.grantTtl",
        label: "Grant lifetime",
        kind: "duration",
        placeholder: "5m",
        hint: "How long an approved grant may be redeemed before it expires unused.",
      },
      {
        path: "approval.maxPending",
        label: "Maximum pending requests",
        kind: "int",
        min: 1,
        placeholder: "1000",
        hint: "A ceiling on the queue, so an approval storm cannot exhaust the instance.",
      },
      {
        path: "approval.sqlSampleBytes",
        label: "Query text in the approval payload (bytes)",
        kind: "int",
        min: 0,
        placeholder: "2048",
        hint: "How much of the statement an approver sees. This is not the audit sample and does not follow the audit privacy profile.",
      },
      {
        path: "approval.persist",
        label: "Survive a restart",
        kind: "bool",
        hint: "Stores pending requests and unredeemed grants on the state volume. A store that cannot be opened fails boot, so this is a durability choice rather than a convenience.",
      },
    ],
  },
  {
    id: "budget",
    title: "Budgets",
    blurb: html`<p class="copy">
      Per-subject daily consumption accounting on the instance's own state volume.
    </p>`,
    fields: [
      {
        path: "budget.enabled",
        label: "Track budgets",
        kind: "bool",
        hint: "Off by default.",
      },
      {
        path: "budget.flushInterval",
        label: "Flush interval",
        kind: "duration",
        placeholder: "5s",
        hint: "How often accumulated counters are written down. A longer interval loses more on an unclean stop.",
      },
      {
        path: "budget.failClosed",
        label: "Fail closed on a store failure",
        kind: "bool",
        hint: "On by default, and it aborts boot if the store cannot be opened — the same shape as the audit pipeline's own fail-closed rule.",
      },
      {
        path: "budget.retentionDays",
        label: "Retention (days)",
        kind: "int",
        min: 1,
        placeholder: "35",
        hint: "How long per-subject counters are kept on the volume.",
      },
    ],
  },
];

const FINDING_TONE: Record<string, BnBadgeVariant> = {
  error: "error",
  "blocking-warning": "warning",
  warning: "warning",
  info: "info",
  hint: "neutral",
};

const GUARD_COLUMNS: BnColumn[] = [
  { key: "path", label: "Key", mono: true, width: "1px" },
  { key: "why", label: "Why it is ours" },
];

/** The scope words, so a refusal and this table read alike. */
const SCOPE_LABELS: Record<string, string> = {
  container_contract: "Container contract",
  control_plane_credential: "Control-plane credential",
  audit_pipeline: "Audit pipeline",
  derived_from_size: "Derived from the size class",
  shared_edge: "Shared edge",
  control_plane_ssrf: "Request-forgery surface",
  rejected_upstream: "Rejected upstream",
};

@customElement("page-server-config")
export class PageServerConfig extends LitElement {
  /** The instance record id, from the route. */
  @property() instanceId = "";

  @state() private config: ServerConfig | null = null;
  /** The edited copy. Replaced wholesale on every keystroke so Lit sees a new
   * object; the spec is a handful of scalars, not a document. */
  @state() private spec: Record<string, unknown> = {};
  @state() private etag = "";
  @state() private loading = true;
  @state() private failure: ApiError | null = null;
  @state() private instance: Instance | null = null;
  /** Whether `GET /v1/instances/{id}` succeeded. It is what distinguishes "this
   * route is not served" from "you cannot read this instance", both of which answer
   * `404`. See [PageServerConfig.load]. */
  @state() private instanceReadable = false;

  /** The rendered artifact, read-only (`SC-F602`). */
  @state() private yaml = "";
  @state() private yamlSource: "" | "current" | "generation" = "";
  @state() private yamlGeneration = 0;

  @state() private dirty = false;
  @state() private saving = false;
  @state() private saveFailure: ApiError | null = null;
  /** Per-path messages, from this form's own checks and from a `422`'s
   * `details.fields`. Keyed by the dotted path, which is what both speak. */
  @state() private fieldErrors: Record<string, string> = {};
  /** The L33 acknowledgement. Reset on every load, never remembered: it is an
   * acknowledgement of *this* save. */
  @state() private acceptDrain = false;
  @state() private confirming = false;

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-block;
      margin-bottom: var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .back:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    header {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .head-text {
      flex: 1 1 26rem;
      min-width: 0;
    }
    h1 {
      margin: 0;
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 0;
      max-width: 72ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    .stack {
      display: grid;
      gap: var(--bino-space-lg);
    }
    p.copy {
      margin: 0 0 var(--bino-space-sm);
      max-width: 72ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    p.copy:last-child {
      margin-bottom: 0;
    }
    p.copy strong {
      color: var(--bino-text);
    }
    .muted {
      margin: var(--bino-space-sm) 0 0;
      max-width: 72ch;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    a.inline {
      color: var(--bino-primary);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    a.inline:hover {
      text-decoration: underline;
    }
    a.inline:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    .grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
      gap: var(--bino-space-md);
      margin-top: var(--bino-space-md);
    }
    .cell {
      min-width: 0;
    }
    .cell .badge-row {
      display: flex;
      gap: var(--bino-space-xs);
      align-items: center;
      margin-bottom: var(--bino-space-xs);
    }
    .cell .inert {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .row {
      display: flex;
      gap: var(--bino-space-sm);
      align-items: center;
      flex-wrap: wrap;
    }
    /* The save bar. A direct child of the host rather than of .stack, and that is
       not arbitrary: a sticky element's containing block is its grid area, which for
       a grid item is exactly its own size, so a sticky bar inside .stack would have
       no range to slide in and would silently behave as static. As a child of the
       host it sticks for the whole length of the page, which is the point — the form
       is long and the statement of what saving costs has to stay on screen with the
       button that does it. */
    .savebar {
      position: sticky;
      bottom: var(--bino-space-md);
      z-index: var(--bino-z-toolbar);
      margin-top: var(--bino-space-lg);
      display: flex;
      align-items: center;
      gap: var(--bino-space-md);
      flex-wrap: wrap;
      padding: var(--bino-space-md);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      background: var(--bino-surface);
      box-shadow: var(--bino-shadow-panel);
    }
    .savebar .spacer {
      flex: 1;
    }
    .clamp {
      padding: var(--bino-space-md);
      border: 1px solid var(--bino-warning-border);
      border-radius: var(--bino-radius);
      background: var(--bino-warning-bg);
      color: var(--bino-warning-text);
    }
    .clamp p {
      margin: 0 0 var(--bino-space-sm);
      max-width: 72ch;
      font-size: var(--bino-font-size-md);
    }
    .clamp p:last-of-type {
      margin-bottom: var(--bino-space-md);
    }
    .clamp code {
      font-family: var(--bino-font-mono);
    }
    /* bn-code sizes to its host, so the host needs a height. */
    .yaml-doc {
      height: 32rem;
    }
    .failure {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-md);
    }
    .failure p {
      margin: 0;
      max-width: 70ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
  `;

  override willUpdate(changed: Map<string, unknown>) {
    if (changed.has("instanceId")) {
      this.config = null;
      this.spec = {};
      this.etag = "";
      this.yaml = "";
      this.yamlSource = "";
      this.failure = null;
      this.saveFailure = null;
      this.fieldErrors = {};
      this.dirty = false;
      this.acceptDrain = false;
      if (this.instanceId) void this.load();
    }
  }

  /**
   * load reads the settings and the instance **together**, and it has to be
   * together rather than one after the other.
   *
   * `404` on the settings route has two possible causes — the route is not served
   * by this build, or this caller cannot read this instance at all — and the page's
   * answer differs completely between them: the first is the product's own gap and
   * the second is a permission or a bad link. The instance read is what tells them
   * apart, so it is not a best-effort enrichment here; it is evidence, and the page
   * cannot classify its own failure without it.
   */
  private async load() {
    this.loading = true;
    const [cfg, inst] = await Promise.allSettled([
      getServerConfigWithETag(this.instanceId),
      getInstance(this.instanceId),
    ]);
    this.instance = inst.status === "fulfilled" ? inst.value : null;
    this.instanceReadable = inst.status === "fulfilled";
    if (cfg.status === "fulfilled") {
      this.config = cfg.value.value;
      this.spec = clone(cfg.value.value.spec ?? {});
      this.etag = cfg.value.etag;
      this.failure = null;
      this.dirty = false;
      this.acceptDrain = false;
      this.fieldErrors = {};
    } else {
      this.failure = parseError(cfg.reason);
      this.config = null;
    }
    this.loading = false;
    void this.loadYaml();
  }

  /**
   * loadYaml reads the rendered artifact for the read-only view.
   *
   * Two sources, in order of freshness: the instance's own rendered
   * `server.yaml`, and — when that route is not answered — the live generation's
   * copy of the same file, which is a different read of the *same* immutable
   * artifact. The fallback is not a workaround: `SC-S611`'s last criterion is that
   * the rendered configuration be viewable as a distinct artifact from the policy
   * bundle, and the generation view satisfies it with a stated generation number,
   * which is arguably the more honest label of the two.
   */
  private async loadYaml() {
    try {
      const view = await getServerConfigYaml(this.instanceId);
      this.yaml = view.yaml;
      this.yamlSource = "current";
      return;
    } catch {
      /* fall through to the generation view */
    }
    try {
      const inst = this.instance ?? (await getInstance(this.instanceId));
      if (inst.generation === null) return;
      const view = await getGenerationYaml(this.instanceId, inst.generation);
      const file = view.files.find((f) => f.path === SERVER_CONFIG_PATH);
      if (!file) return;
      this.yaml = file.content;
      this.yamlSource = "generation";
      this.yamlGeneration = view.generation;
    } catch {
      this.yaml = "";
      this.yamlSource = "";
    }
  }

  /* ── editing ─────────────────────────────────────────────────────────── */

  private valueAt(path: string): unknown {
    return readPath(this.spec, path);
  }

  private setValueAt(path: string, value: unknown) {
    this.spec = writePath(this.spec, path, value);
    this.dirty = true;
    if (this.fieldErrors[path]) {
      const { [path]: _dropped, ...rest } = this.fieldErrors;
      this.fieldErrors = rest;
    }
  }

  /** The two timeouts, in seconds, or null where the key is unset — in which case
   * the platform default applies and it is inside the clamp by construction. */
  private get timeouts(): { query: number | null; max: number | null } {
    return {
      query: durationSeconds(this.valueAt("limits.queryTimeout")),
      max: durationSeconds(this.valueAt("limits.maxQueryTimeout")),
    };
  }

  /** True when the edited configuration lets a query outlive Sluice's fixed drain —
   * the condition rule L33 warns on. It reads the maximum of the two, because what
   * a query can actually reach is whichever of them is larger. */
  private get exceedsClamp(): boolean {
    const { query, max } = this.timeouts;
    const worst = Math.max(query ?? 0, max ?? 0);
    return worst > DRAIN_CLAMP_SECONDS;
  }

  /**
   * validate runs this form's own checks: the per-field bounds of `03` §2.5, its two
   * ordering rules, and the two `SC-F614` conditions that are decidable in the
   * browser.
   *
   * It is deliberately **not** a reimplementation of the server's validator. The
   * control plane mirrors Sluice's own `ServerConfig.Validate()` and the publish gate
   * runs the real `sluice config validate` against the rendered artifact; this pass
   * exists so a typo is caught next to the input rather than as a refusal after a
   * round trip, and every rule it applies is one the server applies too. Where the
   * two ever disagree, the server wins and its message is what is rendered.
   */
  private validate(): boolean {
    const errors: Record<string, string> = {};
    for (const group of FIELDS) {
      for (const field of group.fields) {
        const raw = this.valueAt(field.path);
        if (raw === undefined || raw === null || raw === "") continue;
        switch (field.kind) {
          case "int":
          case "float": {
            const n = Number(raw);
            if (!Number.isFinite(n)) {
              errors[field.path] = "Must be a number.";
            } else if (field.kind === "int" && !Number.isInteger(n)) {
              errors[field.path] = "Must be a whole number.";
            } else if (field.min !== undefined && n < field.min) {
              errors[field.path] = `Must be at least ${field.min}.`;
            } else if (field.max !== undefined && n > field.max) {
              errors[field.path] = `Must be at most ${field.max}.`;
            }
            break;
          }
          case "duration":
            if (durationSeconds(raw) === null) {
              errors[field.path] = "Must be a duration such as 8s, 1500ms, 1m or 1h30m.";
            } else if (durationSeconds(raw) === 0) {
              errors[field.path] = "Must be greater than zero.";
            }
            break;
          default:
            break;
        }
      }
    }

    // The two ordering rules. A ceiling below the default would let a client ask
    // for less than the default and receive more, which is the opposite of a clamp.
    const rows = Number(this.valueAt("limits.maxRows"));
    const ceiling = Number(this.valueAt("limits.maxRowsCeiling"));
    if (Number.isFinite(rows) && Number.isFinite(ceiling) && ceiling < rows) {
      errors["limits.maxRowsCeiling"] = "Must be at least the default row cap.";
    }
    const { query, max } = this.timeouts;
    if (query !== null && max !== null && max < query) {
      errors["limits.maxQueryTimeout"] = "Must be at least the query timeout.";
    }

    // `SC-F614`: tracing enabled without an endpoint is a boot failure upstream, so
    // it is refused before the bundle is rendered rather than discovered when the
    // container exits.
    if (this.valueAt("tracing.enabled") === true && !String(this.valueAt("tracing.endpoint") ?? "")) {
      errors["tracing.endpoint"] =
        "Required whenever tracing is enabled: Sluice refuses this configuration at boot.";
    }
    const members = this.valueAt("policies.composite.members");
    if (
      this.valueAt("policies.engine") === "composite" &&
      (!Array.isArray(members) || members.length === 0)
    ) {
      errors["policies.composite.members"] = "A composite engine needs at least one member.";
    }

    this.fieldErrors = errors;
    return Object.keys(errors).length === 0;
  }

  /**
   * openConfirm is the gate in front of the dialog, and it re-checks both
   * conditions the save bar already disables the button for.
   *
   * That is not redundant. The disabled button is the affordance; this is the rule.
   * A second call site — a keyboard shortcut, a future "save and publish" — would
   * otherwise bypass the L33 acknowledgement, which is the one thing on this screen
   * that must not be bypassable, because the acknowledgement is what gets recorded
   * on the generation.
   */
  private openConfirm() {
    if (!this.validate()) {
      toast("Some values are not valid. The affected fields say why.", "error");
      return;
    }
    if (this.exceedsClamp && !this.acceptDrain) {
      toast(
        "Raising a query timeout past the drain has to be acknowledged before it can be saved.",
        "error",
      );
      return;
    }
    this.saveFailure = null;
    this.confirming = true;
  }

  /**
   * save writes the customer-owned half.
   *
   * It sends the whole edited spec rather than a patch, because `PUT` *replaces* that
   * half — which is also why a key the server never sent is never invented here. The
   * `If-Match` is the tag from the read: a colleague's concurrent edit is a `412`
   * rather than a silent overwrite of their limits.
   */
  private async save() {
    this.saving = true;
    this.saveFailure = null;
    try {
      const answer = await putServerConfig(this.instanceId, this.spec, {
        ifMatch: this.etag || undefined,
      });
      this.config = answer;
      this.spec = clone(answer.spec ?? {});
      this.dirty = false;
      this.confirming = false;
      this.acceptDrain = false;
      // Re-read for the fresh `ETag`, which the response body cannot carry.
      try {
        const { etag } = await getServerConfigWithETag(this.instanceId);
        this.etag = etag;
      } catch {
        this.etag = "";
      }
      void this.loadYaml();
      toast(
        "Saved to the draft. Nothing has restarted yet — publishing the draft is what replaces the running process.",
        "success",
        10000,
      );
    } catch (err) {
      const failure = parseError(err);
      this.saveFailure = failure;
      const fields: Record<string, string> = {};
      for (const f of validationFields(failure)) fields[f.path] = f.reason;
      if (Object.keys(fields).length > 0) {
        this.fieldErrors = { ...this.fieldErrors, ...fields };
        this.confirming = false;
      }
      if (failure.code === "field_guarded") this.confirming = false;
    } finally {
      this.saving = false;
    }
  }

  /* ── rendering ───────────────────────────────────────────────────────── */

  private supportRows(err: ApiError): BnKvRow[] {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    return rows;
  }

  /** One field. The kind decides the control; the hint, the badge and the error all
   * hang off `bn-field` so a form row looks the same everywhere in the product. */
  private renderField(field: Field): TemplateResult {
    const error = this.fieldErrors[field.path] ?? "";
    const raw = this.valueAt(field.path);
    const unset = raw === undefined || raw === null;
    const control = () => {
      switch (field.kind) {
        case "bool":
          return html`<bn-checkbox
            label=${field.label}
            .checked=${raw === true}
            @change=${(e: CustomEvent<{ checked: boolean }>) =>
              this.setValueAt(field.path, e.detail.checked)}
          ></bn-checkbox>`;
        case "enum":
          return html`<bn-select
            .options=${field.options ?? []}
            .value=${typeof raw === "string" ? raw : ""}
            placeholder=${field.placeholder ? `${field.placeholder} (platform default)` : "Select…"}
            @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
              this.setValueAt(field.path, e.detail.value)}
          ></bn-select>`;
        case "members":
          return html`<bn-select
            multiple
            .options=${COMPOSITE_MEMBERS}
            .values=${Array.isArray(raw) ? (raw as string[]) : []}
            placeholder="yaml (platform default)"
            @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
              this.setValueAt(field.path, e.detail.values)}
          ></bn-select>`;
        default:
          return html`<bn-input
            mono
            type=${field.kind === "int" || field.kind === "float" ? "number" : "text"}
            .value=${raw === undefined || raw === null ? "" : String(raw)}
            placeholder=${field.placeholder ?? ""}
            ?invalid=${!!error}
            @input=${(e: CustomEvent<{ value: string }>) =>
              this.setValueAt(field.path, coerce(field.kind, e.detail.value))}
          ></bn-input>`;
      }
    };
    return html`<div class="cell">
      ${field.inert
        ? html`<div class="badge-row">
            <bn-badge variant="not-enforced">Inert in this build</bn-badge>
          </div>`
        : nothing}
      <bn-field
        label=${field.kind === "bool" ? "" : field.label}
        hint=${unset ? `${field.hint} Unset — the platform default applies.` : field.hint}
        .error=${error}
      >
        ${control()}
      </bn-field>
      ${field.inert ? html`<p class="inert">${field.inert}</p>` : nothing}
      ${field.path === "approval.syncWait" ? this.renderSyncWaitEffective() : nothing}
    </div>`;
  }

  /**
   * The approval synchronous wait's *effective* value.
   *
   * Sluice clamps it to the request timeout minus two seconds, silently. `03` §2.5
   * says the editor shows the effective value rather than what was typed, and the
   * reason is that a customer who sets 25 s against a 20 s request timeout has set
   * 18 s and would otherwise never learn it.
   */
  private renderSyncWaitEffective(): TemplateResult | typeof nothing {
    const wait = durationSeconds(this.valueAt("approval.syncWait"));
    const request = durationSeconds(this.valueAt("rest.requestTimeout"));
    if (wait === null || request === null) return nothing;
    const ceiling = request - 2;
    if (wait <= ceiling) return nothing;
    return html`<p class="inert">
      Sluice will clamp this to <code>${ceiling}s</code> — the request timeout minus two seconds —
      so the effective wait is ${ceiling} seconds, not ${wait}.
    </p>`;
  }

  /** The L33 disclosure and its acknowledgement. Shown only when the edited values
   * actually exceed the clamp, so it is never background noise. */
  private renderClamp(): TemplateResult | typeof nothing {
    if (!this.exceedsClamp) return nothing;
    const { query, max } = this.timeouts;
    const worst = Math.max(query ?? 0, max ?? 0);
    return html`<div class="clamp" role="alert">
      <p>
        <strong
          >A query is now allowed to run for ${worst} seconds, and Sluice's drain is a fixed
          10.</strong
        >
        Sluice's REST server drains for a hard-coded 10 seconds on shutdown and its shutdown timeout
        is not configurable, so a query still running when a restart begins is cut off mid-flight.
        Every change to this file is restart-class, so that is not a rare event — it is what happens
        on the next publish.
      </p>
      <p>
        This is a legitimate trade and the product does not refuse it: if you publish rarely and
        query slowly, losing an occasional long query at publish time may be the right choice. It is
        why both timeouts are rendered at ${DRAIN_CLAMP_SECONDS} seconds by default rather than at
        Sluice's own 30, and why raising them is acknowledged rather than merely allowed. The
        acknowledgement is recorded on the generation this produces.
      </p>
      <bn-checkbox
        label="I accept that a query running longer than the drain is dropped on every restart"
        .checked=${this.acceptDrain}
        @change=${(e: CustomEvent<{ checked: boolean }>) => (this.acceptDrain = e.detail.checked)}
      ></bn-checkbox>
    </div>`;
  }

  /**
   * The guarded set (`SC-F613`, `SC-S612`).
   *
   * The rows are the *union* of what the server reported and what this bundle knows,
   * because the two can legitimately differ: a server whose guarded set is wider is
   * the normal direction of drift, and a key it reported that this bundle has no
   * reason for still renders read-only with the generic statement. The set is never
   * *narrowed* by the client — a key this bundle believes is guarded is shown as
   * guarded even if the response omitted it, because offering to edit something the
   * server will refuse is worse than refusing to offer it.
   */
  private renderGuarded(): TemplateResult {
    const reported = this.config?.guarded_fields ?? [];
    const paths = [...new Set([...GUARDED_KEYS.map((k) => k.path), ...reported])].sort();
    const rows: BnRow[] = paths.map((path) => {
      const key = guardReason(path);
      return {
        path,
        why: html`<span>${key.reason}</span>
          <span style="display:block; margin-top: var(--bino-space-xs)">
            <bn-badge variant="neutral">${SCOPE_LABELS[key.scope] ?? key.scope}</bn-badge>
          </span>`,
      };
    });
    return html`<bn-card heading="Settings Sluice Cloud owns" flush>
      <div style="padding: var(--bino-space-md) var(--bino-space-md) 0">
        <p class="copy">
          These keys are rendered into your <code>server.yaml</code> and are
          <strong>refused, not ignored</strong>, if a request tries to set them: the answer is
          <code>field_guarded</code> naming every offending key. Silently dropping a submitted value
          would let you believe you had changed the audit destination when you had not.
        </p>
        <p class="copy">
          The refusal is the same for an owner. A guard a role can talk its way past is not a guard —
          and most of these are not privileges we are withholding, they are values whose only safe
          setting is the one computed beside them.
        </p>
        <p class="copy">
          Credential-bearing keys — the admin token, the API-key pepper, the audit genesis seed —
          exist on your instance and are never sent to a browser. The rendered file below shows the
          admin token as a fixed placeholder for the same reason: this is a viewer-role read, and the
          token grants the instance's admin plane.
        </p>
      </div>
      <bn-table
        caption="Control-plane-owned server settings"
        .columns=${GUARD_COLUMNS}
        .rows=${rows}
      ></bn-table>
    </bn-card>`;
  }

  /** The read-only derived block, plus the size-derived numbers `SC-S301` requires
   * to be shown with the size that produced them. */
  private renderDerived(): TemplateResult | typeof nothing {
    const derived = this.config?.derived ?? {};
    const rows: BnKvRow[] = flatten(derived).map(([label, value]) => ({ label, value }));
    const size = this.instance ? sizeClass(this.instance.size) : undefined;
    if (rows.length === 0 && !size) return nothing;
    return html`<bn-card heading="Values Sluice Cloud computed">
      ${size
        ? html`<p class="copy">
              Derived from this instance's size class, <code>${this.instance?.size}</code>. The
              container's memory limit, the engine's own memory limit and its thread count come from
              one table row together, because a DuckDB limit above the container's limit is a
              container that gets killed instead of spilling to disk, and a thread count above the
              scheduled cores is throttling rather than throughput.
            </p>
            <bn-kv
              .rows=${[
                { label: "duckdb.memoryLimit", value: size.duckdb_memory },
                { label: "duckdb.threads", value: String(size.duckdb_threads) },
                { label: "Container memory", value: size.memory },
                { label: "Container CPU", value: size.cpu },
              ] as BnKvRow[]}
            ></bn-kv>
            <p class="muted">
              The size class is changed on
              <a class="inline" href=${hrefFor("instances", this.instanceId, "settings")}
                >instance settings</a
              >, which is also a restart-class change.
            </p>`
        : nothing}
      ${rows.length > 0
        ? html`<div style="margin-top: var(--bino-space-lg)">
            <bn-kv .rows=${rows}></bn-kv>
          </div>`
        : nothing}
    </bn-card>`;
  }

  /** The linter's own findings on the stored object, as the API returned them. */
  private renderFindings(): TemplateResult | typeof nothing {
    const findings = this.config?.findings ?? [];
    if (findings.length === 0) return nothing;
    return html`<bn-card heading="Findings">
      ${findings.map(
        (f: PolicyFinding) => html`<div style="margin-bottom: var(--bino-space-md)">
          <div class="row">
            <bn-badge .variant=${FINDING_TONE[f.severity] ?? "neutral"}>${f.severity}</bn-badge>
            ${f.rule ? html`<code>${f.rule}</code>` : nothing}
            ${f.path ? html`<code>${f.path}</code>` : nothing}
          </div>
          <p class="copy" style="margin-top: var(--bino-space-xs)">${f.message}</p>
          ${f.explanation ? html`<p class="muted">${f.explanation}</p>` : nothing}
          ${f.checked_at_publish
            ? html`<p class="muted">
                Decided against the instance this is published to, so it is checked at publish rather
                than now.
              </p>`
            : nothing}
        </div>`,
      )}
    </bn-card>`;
  }

  /** The rendered artifact (`SC-F602`), read-only, with the redaction stated. */
  private renderYaml(): TemplateResult | typeof nothing {
    if (this.yaml === "") return nothing;
    return html`<bn-card
      heading=${this.yamlSource === "generation"
        ? `Rendered server.yaml — generation ${this.yamlGeneration}`
        : "Rendered server.yaml"}
    >
      <p class="copy">
        This is the whole artifact, both halves, as the container reads it. It is a
        <strong>separate file</strong> from the policy directory with its own hash, and it is
        rendered completely rather than partially: Sluice binds an environment override only for keys
        that have a registered default or are already present in the file, so a partial file plus
        overrides is a container that starts, serves traffic and is configured differently from what
        the control plane believes — with no error and no log line.
      </p>
      <p class="copy">
        <code>admin.token</code> is shown as a fixed placeholder. The hashes beside a generation are
        computed over the <strong>real</strong> bytes, so the text you see does not hash to the
        digest you see — hashing the redacted text instead would silently break the publish
        confirmation, which is the one thing the digest is for.
      </p>
      ${this.yamlSource === "generation"
        ? html`<p class="muted">
            This is the copy stored with generation ${this.yamlGeneration}, which is what the
            instance is running. Unpublished edits above are not in it yet.
          </p>`
        : nothing}
      <div class="yaml-doc" style="margin-top: var(--bino-space-md)">
        <bn-code
          language="yaml"
          label="Rendered server.yaml"
          filename="server.yaml"
          .value=${this.yaml}
        ></bn-code>
      </div>
    </bn-card>`;
  }

  /** The save bar, which is also where the restart statement lives. */
  private renderSaveBar(): TemplateResult {
    const blocked = this.exceedsClamp && !this.acceptDrain;
    return html`<div class="savebar">
      <bn-badge variant="restart">Restart-class</bn-badge>
      <span class="muted" style="margin: 0; max-width: 46ch">
        Saving writes the draft. The publish that ships it replaces the running process — nothing in
        this file hot-reloads.
      </span>
      <span class="spacer"></span>
      ${this.dirty
        ? html`<bn-button ?disabled=${this.saving} @click=${() => void this.load()}>
            Discard my edits
          </bn-button>`
        : nothing}
      <bn-button
        variant="primary"
        ?disabled=${!this.dirty || blocked || this.saving}
        ?loading=${this.saving}
        @click=${() => this.openConfirm()}
      >
        Save to the draft
      </bn-button>
    </div>`;
  }

  private renderConfirm(): TemplateResult | typeof nothing {
    if (!this.confirming) return nothing;
    const engineChanged =
      String(this.valueAt("policies.engine") ?? "") !==
      String(readPath(this.config?.spec ?? {}, "policies.engine") ?? "");
    return html`<bn-modal
      open
      .width=${640}
      tone="destructive"
      heading="Save these server settings to the draft?"
      @close=${() => (this.confirming = false)}
    >
      <p class="copy">
        This writes the draft generation. <strong>Nothing restarts now</strong> — the draft is not
        running anywhere. What it does is make the next publish restart-class: every key in this file
        takes effect only when the process starts, so shipping it replaces the running task.
      </p>
      ${engineChanged
        ? html`<p class="copy">
            <strong>You are changing the policy engine.</strong> That changes how a query is decided,
            not how fast it is decided. Review your policies and run the test suite before publishing
            this — the linter cannot tell you what a different engine would have allowed.
          </p>`
        : nothing}
      ${this.exceedsClamp
        ? html`<p class="copy">
            The query-timeout acknowledgement you gave above is recorded on the generation this
            produces, so the trade is attributable rather than assumed.
          </p>`
        : nothing}
      <p slot="consequence">
        The publish screen states the exact interruption before you confirm it, quantifies the
        query-loss window against what is live now, and requires an explicit acceptance of the
        restart. Saving here commits you to nothing except the values.
      </p>
      <bn-button slot="footer" @click=${() => (this.confirming = false)}>Cancel</bn-button>
      <bn-button
        slot="footer"
        variant="primary"
        ?loading=${this.saving}
        @click=${() => void this.save()}
      >
        Save to the draft
      </bn-button>
    </bn-modal>`;
  }

  /**
   * The refusal.
   *
   * `field_guarded` is the one code that needs its own rendering rather than its
   * catalogue line: the whole point of `SC-F613` is that the refusal *names the
   * keys*, and `details.fields` carries them with `details.reasons` explaining each.
   * Rendering it as "one or more of those settings are managed by Sluice Cloud"
   * would throw away exactly the part that lets a customer fix a pasted
   * configuration in one pass.
   */
  private renderSaveFailure(err: ApiError): TemplateResult {
    const guarded = errorDetail<string[]>(err, "fields") ?? [];
    return html`<bn-card heading="These settings were not saved">
      <div role="alert">
        <p class="copy">${err.serverMessage || err.message}</p>
        ${err.code === "field_guarded" && guarded.length > 0
          ? html`<p class="copy">
                The refusal names every offending key rather than the first, so a pasted
                configuration can be fixed in one pass:
              </p>
              <bn-kv
                .rows=${guarded.map((path) => ({
                  label: path,
                  value: guardReason(path).reason,
                })) as BnKvRow[]}
              ></bn-kv>`
          : nothing}
        ${err.code === "precondition_failed"
          ? html`<p class="copy">
              Somebody else changed these settings since the page loaded, so your edit was refused
              rather than applied over theirs. Reload to pick up their change, then re-apply yours.
            </p>`
          : nothing}
        ${err.code === "policy_invalid"
          ? html`<p class="copy">
              The control plane refuses at save time everything Sluice would refuse at container
              boot. Discovering one of these when the container exits is an outage, and a mysterious
              one — the rollout rolls back and the cause is a line in a log you cannot read.
            </p>`
          : nothing}
        <bn-kv .rows=${this.supportRows(err)}></bn-kv>
      </div>
    </bn-card>`;
  }

  /**
   * The read-only state for a build that does not serve the editor.
   *
   * `GET`/`PUT /v1/instances/{id}/server-config` are specified in `06` §3.9 and are
   * staged in this build (`internal/pat`'s own staged-route set names them). A form
   * that posted into a route nobody answers would be the product claiming a
   * capability it does not have, so the page says which route is missing, keeps the
   * *readable* half — the guarded set with its reasons, and the rendered artifact
   * from the live generation — and names the surface that carries the capability
   * today. When the route lands this branch simply stops being taken; nothing here
   * has to change.
   */
  private renderUnavailable(err: ApiError): TemplateResult {
    return html`
      <a class="back" href=${hrefFor("instances", this.instanceId)}>← Instance</a>
      <header>
        <div class="head-text">
          <h1>Server settings</h1>
          <p class="lede">
            The tunables inside this instance's <code>server.yaml</code>: query limits, rate limits,
            the rewrite cache, the policy engine, logging, tracing, approvals and budgets.
          </p>
        </div>
      </header>
      <div class="stack">
        <bn-empty-state
          icon="◌"
          heading="Editing these settings is not in this build"
          message="The form is specified and the route that serves it has not landed, so there is nothing here to post into. Until it does, a server-settings change is made by support — which is also why every value in force is readable below rather than hidden."
        >
          <bn-button
            slot="actions"
            variant="primary"
            @click=${() => navigate(hrefFor("instances", this.instanceId))}
          >
            Open the instance console
          </bn-button>
        </bn-empty-state>
        <bn-panel eyebrow="What is readable today">
          <p class="copy">
            The whole rendered artifact is below, from the live generation, with the admin token
            redacted. The guarded half is listed with the reason each key is ours. Every published
            generation keeps its own copy of this file, so
            <a class="inline" href=${hrefFor("instances", this.instanceId, "generations")}
              >the generation history</a
            >
            answers "what were the limits in March" exactly.
          </p>
          <bn-kv .rows=${this.supportRows(err)}></bn-kv>
        </bn-panel>
        ${this.renderYaml()} ${this.renderGuarded()} ${this.renderDerived()}
      </div>
    `;
  }

  override render() {
    if (this.loading && !this.config) {
      return html`<bn-spinner block size="28" label="Loading the server settings"></bn-spinner>`;
    }
    if (this.failure) {
      // A route this build does not serve is a different statement from a route that
      // refused this caller, and only the first one is the product's own gap. The
      // instance read is the evidence: if the instance itself is readable and only
      // its settings answered 404, the missing thing is the route.
      if (this.failure.status === 501 || (this.failure.code === "not_found" && this.instanceReadable)) {
        return this.renderUnavailable(this.failure);
      }
      return html`
        <a class="back" href=${hrefFor("instances", this.instanceId)}>← Instance</a>
        <bn-card heading="The server settings could not be loaded">
          <div class="failure" role="alert">
            <p>${this.failure.message}</p>
            <bn-kv .rows=${this.supportRows(this.failure)}></bn-kv>
            <div>
              <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
            </div>
          </div>
        </bn-card>
      `;
    }

    return html`
      <a class="back" href=${hrefFor("instances", this.instanceId)}>← Instance</a>
      <header>
        <div class="head-text">
          <h1>
            Server settings
            <bn-badge variant="restart">Restart-class</bn-badge>
          </h1>
          <p class="lede">
            The tunables inside this instance's <code>server.yaml</code>. Everything here takes
            effect when the process starts — <strong>nothing in this file hot-reloads</strong> — so
            saving writes the draft and publishing the draft replaces the running task. The keys
            Sluice Cloud owns are listed at the bottom with the reason each one is ours.
          </p>
        </div>
        <div>
          <bn-button @click=${() => navigate(hrefFor("instances", this.instanceId, "publish"))}>
            Review the draft
          </bn-button>
        </div>
      </header>

      <div class="stack">
        ${this.saveFailure ? this.renderSaveFailure(this.saveFailure) : nothing}
        ${this.renderFindings()}
        ${FIELDS.map(
          (group) => html`<bn-card heading=${group.title}>
            ${group.blurb}
            <div class="grid">${group.fields.map((f) => this.renderField(f))}</div>
          </bn-card>`,
        )}
        ${this.renderClamp()} ${this.renderDerived()} ${this.renderGuarded()} ${this.renderYaml()}
        ${this.config
          ? html`<bn-panel eyebrow="Last change">
              <p class="copy">
                ${this.config.updated_by_label
                  ? `Last saved by ${this.config.updated_by_label}`
                  : "Last saved"}
                ${this.config.updated ? ` on ${when(this.config.updated)}` : ""}. The attribution is
                a snapshot taken at edit time, so a colleague who has since left the organization is
                still named.
              </p>
            </bn-panel>`
          : nothing}
      </div>
      ${this.renderSaveBar()} ${this.renderConfirm()}
    `;
  }
}

/* ── dotted-path access over the spec object ───────────────────────────── */

/** readPath resolves a dotted path, answering undefined for any missing level. */
function readPath(spec: Record<string, unknown>, path: string): unknown {
  let node: unknown = spec;
  for (const segment of path.split(".")) {
    if (node === null || typeof node !== "object") return undefined;
    node = (node as Record<string, unknown>)[segment];
  }
  return node;
}

/**
 * writePath returns a copy of `spec` with `path` set, creating the intermediate
 * objects it needs.
 *
 * A copy rather than a mutation, because the spec is Lit `@state`: mutating it in
 * place would leave the rendered form and the object that gets sent out of step
 * with each other, which is the class of bug where a field looks edited and saves
 * its old value.
 *
 * An empty string DELETES the key rather than writing `""`. That is the correct
 * semantics for this form: `PUT` replaces the customer-owned half and the server
 * defaults an absent key from `bundle.DefaultCustomerFields`, so clearing a field
 * means "use the platform default" — while writing an empty string would mean
 * "zero", which for a timeout is not the same thing at all and for a duration is
 * not even valid.
 */
function writePath(
  spec: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const segments = path.split(".");
  const out: Record<string, unknown> = { ...spec };
  let node = out;
  for (let i = 0; i < segments.length - 1; i++) {
    const key = segments[i]!;
    const child = node[key];
    node[key] = child && typeof child === "object" ? { ...(child as Record<string, unknown>) } : {};
    node = node[key] as Record<string, unknown>;
  }
  const leaf = segments[segments.length - 1]!;
  if (value === "" || value === undefined) delete node[leaf];
  else node[leaf] = value;
  return out;
}

/** clone deep-copies the server's spec so editing cannot mutate the loaded copy the
 * "discard my edits" path restores from. */
function clone(spec: Record<string, unknown>): Record<string, unknown> {
  return JSON.parse(JSON.stringify(spec ?? {})) as Record<string, unknown>;
}

/**
 * coerce turns an input's text into the JSON type the key holds.
 *
 * Numbers go over the wire as numbers and durations as strings, because that is
 * what `server.yaml` holds and what the server's decoder expects. A number field
 * whose text is not yet a number — mid-typing "1e", or a lone minus sign — keeps
 * the raw text so the input does not fight the typist; [PageServerConfig.validate]
 * is what refuses it at save time.
 */
function coerce(kind: FieldKind, text: string): unknown {
  if (text === "") return "";
  if (kind === "int" || kind === "float") {
    const n = Number(text);
    return Number.isFinite(n) ? n : text;
  }
  return text;
}

/** flatten turns the derived object into `[dotted path, value]` pairs for a bn-kv,
 * so a shape this bundle does not model still renders every value it carries rather
 * than dropping the ones it did not expect. */
function flatten(node: Record<string, unknown>, prefix = ""): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  for (const [key, value] of Object.entries(node)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      out.push(...flatten(value as Record<string, unknown>, path));
    } else {
      out.push([path, Array.isArray(value) ? value.join(", ") : String(value)]);
    }
  }
  return out;
}

/** A Go duration literal: one or more `<number><unit>` components. */
const DURATION = /^(\d+(?:\.\d+)?(?:ns|us|µs|ms|s|m|h))+$/;

const UNIT_SECONDS: Record<string, number> = {
  ns: 1e-9,
  us: 1e-6,
  "µs": 1e-6,
  ms: 1e-3,
  s: 1,
  m: 60,
  h: 3600,
};

/**
 * durationSeconds parses a Go duration literal to seconds, answering null for
 * anything that is not one.
 *
 * The form edits durations as the literals the YAML holds — `8s`, `1m30s`, `500ms`
 * — rather than as a number plus a unit picker, so a reader can hold this form and
 * the rendered file side by side and see the same strings. The parse exists only for
 * the comparisons that need an ordering: the drain clamp, the timeout ordering rule
 * and the approval clamp.
 */
function durationSeconds(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string" || value === "") return null;
  if (!DURATION.test(value)) return null;
  let total = 0;
  for (const [, amount, unit] of value.matchAll(/(\d+(?:\.\d+)?)(ns|us|µs|ms|s|m|h)/g)) {
    total += Number(amount) * (UNIT_SECONDS[unit!] ?? 0);
  }
  return total;
}

/** when renders a timestamp in the reader's locale, falling back to the raw value
 * rather than to "Invalid Date". */
function when(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

declare global {
  interface HTMLElementTagNameMap {
    "page-server-config": PageServerConfig;
  }
}
